import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Pencil, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  getAdminMerchantsGetAdminMerchantsQueryKey,
  useAdminMerchantsGetAdminMerchants,
  useAdminMerchantsPostAdminMerchantsIdGuidActivate,
  useAdminMerchantsPostAdminMerchantsIdGuidSuspend,
} from '@/lib/api/admin-merchants/admin-merchants';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useHasRole } from '@/lib/auth/useAuth';
import { EditMerchantDialog } from './EditMerchantDialog';

/**
 * Global merchants list across every client. Uses the admin-only
 * GET /admin/merchants endpoint so SuperAdmin/Admin can find and
 * support any partner's merchant without drilling through the Clients page.
 */
export default function MerchantsAdminPage() {
  const canEdit = useHasRole('Admin');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'suspended'>('all');
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useAdminMerchantsGetAdminMerchants({
    page,
    limit: 25,
    search: search || undefined,
    status: status === 'all' ? undefined : status,
  });

  const pages = useMemo(() => {
    if (!data) return 1;
    const total = Number(data.total);
    const limit = Number(data.limit) || 1;
    return Math.max(1, Math.ceil(total / limit));
  }, [data]);

  const suspend = useAdminMerchantsPostAdminMerchantsIdGuidSuspend();
  const activate = useAdminMerchantsPostAdminMerchantsIdGuidActivate();

  const toggle = async (id: string, isActive: boolean) => {
    try {
      if (isActive) await suspend.mutateAsync({ id });
      else await activate.mutateAsync({ id });
      await queryClient.invalidateQueries({ queryKey: getAdminMerchantsGetAdminMerchantsQueryKey() });
    } catch {
      toast.error('Could not change merchant status.');
    }
  };

  return (
    <section className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Merchants</h1>
        <p className="text-sm text-muted-foreground">
          Every merchant across every client. Drill into a client's page for the per-client view.
        </p>
      </header>

      <Card>
        <CardHeader className="flex flex-row items-center gap-3 space-y-0">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            value={pendingSearch}
            placeholder="Search by merchant name or code…"
            onChange={(e) => setPendingSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                setSearch(pendingSearch.trim());
              }
            }}
            className="max-w-sm"
          />
          <select
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as typeof status);
              setPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          {search && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                setPendingSearch('');
                setPage(1);
              }}
            >
              Clear
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {isError ? (
            <div className="p-6">
              <CardTitle className="text-destructive">Could not load merchants</CardTitle>
              <CardDescription>{(error as Error)?.message ?? 'Unknown error'}</CardDescription>
            </div>
          ) : isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : !data || data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No merchants match the filter.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Client</th>
                  <th className="p-3 font-medium">Merchant</th>
                  <th className="p-3 font-medium">Merchant ID</th>
                  <th className="p-3 font-medium">Stores</th>
                  <th className="p-3 font-medium">Status</th>
                  {canEdit && <th className="w-28 p-3" />}
                  <th className="w-8 p-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((m) => (
                  <tr
                    key={m.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => navigate(`/merchants/${m.id}`)}
                  >
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <Link to={`/clients/${m.clientId}`} className="block hover:underline">
                        <div className="font-medium">{m.clientName}</div>
                        <div className="font-mono text-xs text-muted-foreground">{m.clientExternalId}</div>
                      </Link>
                    </td>
                    <td className="p-3 font-medium">{m.name}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{m.merchantId}</td>
                    <td className="p-3 tabular-nums">{m.storeCount}</td>
                    <td className="p-3">
                      {m.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="warning">Suspended</Badge>}
                    </td>
                    {canEdit && (
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggle(m.id, m.isActive)}
                            disabled={suspend.isPending || activate.isPending}
                          >
                            {m.isActive ? 'Suspend' : 'Activate'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${m.name}`}
                            onClick={() => setEditingId(m.id)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    )}
                    <td className="p-3 text-muted-foreground">
                      <ChevronRight className="h-4 w-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
        {data && data.total > data.limit && (
          <div className="flex items-center justify-between border-t border-border p-3 text-xs text-muted-foreground">
            <span>
              Page {page} of {pages} · {data.total} total
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {canEdit && (
        <EditMerchantDialog
          merchantId={editingId}
          onOpenChange={(open) => !open && setEditingId(null)}
        />
      )}
    </section>
  );
}
